import localhost from "apis/localhost";
import {
  addToJobQueue,
  clearJobQueue,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import {
  addChecked,
  expand,
  fetchItems,
  fetchUniqueItemsCount,
  filterItems,
} from "slices/discovery/actions/theme";
import {
  addIgnoredDocument,
  removeIgnoredDocument,
  setDisableSaveSnapshot,
  setFilterDocID,
  setFilterType,
  setFilterView,
  setSearch,
  setSearchTerm,
  setSnapshots,
} from "slices/discovery/discoverySlice";
import { populateErrorMessage } from "slices/app/errorSlice";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal } from "slices/app/actions/modals";
import { modalTypes } from "constants/types";
import { alertVariants } from "@visiblethread/react-components/dist/components/Alert";
import {
  applyFilter,
  clearSearchAndFilterState,
} from "slices/discovery/actions/filters";
import { showSelectedLoadThemes } from "./config";

export const fetchSnapshots = () => async (dispatch, getState) => {
  dispatch(addToJobQueue("fetchSnapshots"));

  const { folderId, documentId } = getState().discovery.config;

  await localhost
    .get("/docs/snapshot/all", {
      params: {
        folderId,
        documentId,
      },
    })
    .then(({ data }) => {
      dispatch(setSnapshots(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(removeFromJobQueue("fetchSnapshots"));
    });
};

export const saveSnapshot = (name) => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const {
    themes,
    config: {
      folderId,
      documentId,
      showAll,
      filterType,
      filterDocID,
      searchTerm,
    },
  } = getState().discovery;

  const checkedDocuments = themes[0]?.Documents.filter(
    (doc) => !doc.ignored
  ).map((doc) => doc.Id);

  const selectedItems = themes
    .filter((element) => element.NounPhrase && element.checked)
    .map((item) => ({
      GroupName: item.GroupName,
      NounPhrase: item.NounPhrase,
    }));

  const groupNamesWithSelectedItems = Array.from(
    new Set(selectedItems.map((item) => item.GroupName))
  );

  const expandedGroups = themes
    .filter(
      (element) =>
        !element.NounPhrase &&
        groupNamesWithSelectedItems.includes(element.GroupName)
    )
    .map((item) => ({
      groupName: item.GroupName,
      // checked: item.checked === undefined ? false : item.checked,
      checked: false,
      checkedNounPhrases: selectedItems
        .filter((selectedItem) => selectedItem.GroupName === item.GroupName)
        .map((selectedItem) => selectedItem.NounPhrase),
    }));

  const snapshot = {
    name,
    checkedDocuments,
    expandedGroups,
    documentId,
    showAll,
    searchTerm,
    discoveryFilter: filterType,
    discoveryFilterDocumentId: filterDocID,
  };

  await localhost
    .post("/docs/snapshot", {
      folderId,
      snapshot,
    })
    .then(() => {
      dispatch(fetchSnapshots());
      dispatch(setDisableSaveSnapshot(true));
      dispatch(hideModal(modalTypes.SAVE_SNAPSHOT));
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `“${name}” successfully saved as a snapshot`,
        })
      );
    })
    .catch((error) => {
      dispatch(populateErrorMessage(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

const ignoreDocsForSnapshot = (snapshot) => (dispatch, getState) => {
  if (!snapshot.checkedDocuments) return;

  const snapshotState = getState().discovery.themes;
  snapshotState[0].Documents.filter(
    (doc) => !snapshot.checkedDocuments.includes(doc.Id)
  ).forEach((doc) => {
    dispatch(addIgnoredDocument(doc.Id));
  });
};

const checkAndOrExpandThemesForSnapshot =
  (snapshot) => (dispatch, getState) => {
    if (!snapshot.expandedGroups) return;

    const snapshotState = getState().discovery.themes;
    snapshot.expandedGroups.forEach((group) => {
      snapshotState.forEach((theme) => {
        if (theme.GroupName === group.groupName && !theme.NounPhrase) {
          // check and expand if not already done
          if (group.checked && !theme.checked) {
            dispatch(addChecked(theme));
          }
          if (!group.checked) {
            const areItemsCollapsed = snapshotState
              .filter(
                (element) =>
                  element.GroupName === theme.GroupName && element.NounPhrase
              )
              .map((item) => item.collapsed);

            // just expand if items have not been expanded yet
            if (!areItemsCollapsed.includes(false)) {
              dispatch(expand(theme, snapshot));
            }
          }
        }
      });
    });
  };

const checkItemsForSnapshot = (snapshot) => (dispatch, getState) => {
  if (!snapshot.expandedGroups) return;

  const snapshotState = getState().discovery.themes;
  snapshot.expandedGroups.forEach((group) => {
    snapshotState.forEach((theme) => {
      if (theme.GroupName === group.groupName && theme.NounPhrase) {
        if (
          !theme.checked &&
          group.checkedNounPhrases.includes(theme.NounPhrase)
        ) {
          dispatch(addChecked(theme));
        }
      }
    });
  });
};

export const getSnapshotFromPreparedData = (snapshot) => (dispatch) => {
  dispatch(ignoreDocsForSnapshot(snapshot));
  dispatch(checkAndOrExpandThemesForSnapshot(snapshot));
  dispatch(checkItemsForSnapshot(snapshot));
};

// Might need rework
const chackAllDocs = () => (dispatch, getState) => {
  const snapshotState = getState().discovery.themes;
  snapshotState[0].Documents.forEach((doc) => {
    if (doc.ignored) {
      dispatch(removeIgnoredDocument(doc.Id));
    }
  });
};

export const clearSearchAndFilterFlags = () => (dispatch) => {
  dispatch(setFilterView(false));
  dispatch(setFilterType(null));
  dispatch(setFilterDocID(null));
  dispatch(setSearch(false));
  dispatch(setSearchTerm(""));
};

export const applySnapshot = (snapshot) => (dispatch) => {
  dispatch(addToJobQueue("genericJob"));
  dispatch(chackAllDocs());
  dispatch(ignoreDocsForSnapshot(snapshot));
  dispatch(checkAndOrExpandThemesForSnapshot(snapshot));
  dispatch(checkItemsForSnapshot(snapshot));
  dispatch(showSelectedLoadThemes(snapshot.showAll));
  dispatch(setDisableSaveSnapshot(true));
  dispatch(clearJobQueue());
};

export const fetchSnapshot = (snapshotName) => async (dispatch, getState) => {
  dispatch(addToJobQueue("fetchSnapshot"));

  await localhost
    .get("/docs/snapshot", {
      params: {
        snapshotName,
        folderId: getState().discovery.config.folderId,
      },
    })
    .then(async (response) => {
      const snapshot = response.data;

      dispatch(fetchSnapshots());
      dispatch(clearSearchAndFilterFlags());

      if (snapshot.discoveryFilter) {
        await dispatch(
          applyFilter(
            snapshot.discoveryFilter,
            snapshot.discoveryFilterDocumentId,
            snapshot.searchTerm,
            null,
            snapshot
          )
        );
        dispatch(fetchUniqueItemsCount(snapshot.discoveryFilter));
      } else if (snapshot.searchTerm) {
        await dispatch(filterItems(snapshot.searchTerm, null, snapshot));
        dispatch(fetchUniqueItemsCount());
      } else {
        await dispatch(clearSearchAndFilterState(false));
        await dispatch(fetchItems(snapshot));
        await dispatch(applySnapshot(snapshot));
        dispatch(fetchUniqueItemsCount());
        dispatch(removeFromJobQueue("fetchSnapshot"));
      }
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    });
};

export const deleteSnapshot = (snapshotName) => async (dispatch, getState) => {
  dispatch(addToJobQueue("genericJob"));

  const params = {
    snapshotName,
    folderId: getState().discovery.config.folderId,
  };

  await localhost
    .delete("/docs/snapshot", { params })
    .then(() => {
      dispatch(fetchSnapshots());
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `“${snapshotName}” successfully deleted as a snapshot`,
        })
      );
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};
